import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { register } from '../lib/api';
import { toast } from 'sonner';
import { UserPlus, ArrowRight, ArrowLeft, Eye, EyeOff, Mail, Building2, User, Lock } from 'lucide-react';
import GoogleSignInButton from '../components/GoogleSignInButton';
import AppleSignInButton from '../components/AppleSignInButton';
import AuthLayout, { authInputClass } from '../components/AuthLayout';

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    companyName: '',
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateForm = () => {
    if (!formData.companyName.trim()) {
      toast.error('Please enter your company name');
      return false;
    }
    if (!formData.name.trim()) {
      toast.error('Please enter your name');
      return false;
    }
    if (!formData.email.trim()) {
      toast.error('Please enter your email');
      return false;
    }
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const response = await register(
        formData.companyName,
        formData.name,
        formData.email,
        formData.password
      );

      if (response.data && response.data.ok) {
        setSuccess(true);
        toast.success('Please check your email to verify your account');
      } else if (response.data && response.data.message) {
        toast.error(response.data.message);
      } else {
        toast.error('Registration failed. Please try again.');
      }
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = error.response?.data?.detail ||
                          error.response?.data?.message ||
                          error.message ||
                          'Registration failed. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <AuthLayout testId="register-page">
        <div className="text-center" data-testid="register-success">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center">
            <Mail size={30} className="text-white" />
          </div>
          <h2 className="text-xl font-extrabold text-slate-900 mb-2">Check Your Email</h2>
          <p className="text-sm text-slate-600 mb-1">
            We've sent a verification link to
          </p>
          <p className="text-sm font-semibold text-slate-800 mb-4 break-all">{formData.email}</p>
          <p className="text-xs text-slate-500 mb-5">
            Click the link in your email to activate your account. The link expires in 24 hours.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="w-full h-12 rounded-2xl font-bold text-base text-white flex items-center justify-center gap-2 bg-gradient-to-r from-[#57B22E] to-[#3E9A1C] shadow-lg shadow-green-600/30 hover:from-[#4CA327] hover:to-[#358916] active:scale-[0.99] transition-all"
          >
            Go to Login
            <ArrowRight size={18} />
          </button>
          <p className="text-xs text-slate-400 mt-3">
            Didn't receive the email? Check your spam folder or{' '}
            <button onClick={() => setSuccess(false)} className="text-blue-600 hover:text-blue-700 font-medium">
              try again
            </button>
          </p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout testId="register-page">
      <div className="text-center mb-4">
        <h1 className="text-xl font-extrabold text-slate-900">Create your account</h1>
        <p className="text-sm text-slate-500 mt-0.5">Start your 7-day free trial</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3" data-testid="register-form">
        {/* Company Name */}
        <div>
          <label className="block text-sm font-bold text-slate-800 mb-1.5">Company Name</label>
          <div className="relative">
            <Building2 size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10" />
            <input
              type="text"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              required
              placeholder="Your Company Ltd"
              data-testid="company-name-input"
              className={`${authInputClass} pl-9 pr-3`}
            />
          </div>
        </div>

        {/* Full Name */}
        <div>
          <label className="block text-sm font-bold text-slate-800 mb-1.5">Your Name</label>
          <div className="relative">
            <User size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10" />
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="John Smith"
              data-testid="name-input"
              className={`${authInputClass} pl-9 pr-3`}
            />
          </div>
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-bold text-slate-800 mb-1.5">Email Address</label>
          <div className="relative">
            <Mail size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10" />
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="you@company.com"
              data-testid="email-input"
              className={`${authInputClass} pl-9 pr-3`}
            />
          </div>
        </div>

        {/* Password */}
        <div>
          <label className="block text-sm font-bold text-slate-800 mb-1.5">Password</label>
          <div className="relative">
            <Lock size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10" />
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              placeholder="Min. 6 characters"
              data-testid="password-input"
              className={`${authInputClass} pl-9 pr-10`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 inset-y-0 flex items-center text-slate-400 hover:text-slate-600 z-10"
              data-testid="toggle-password"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        {/* Confirm Password */}
        <div>
          <label className="block text-sm font-bold text-slate-800 mb-1.5">Confirm Password</label>
          <div className="relative">
            <Lock size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10" />
            <input
              type={showPassword ? 'text' : 'password'}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              placeholder="Confirm your password"
              data-testid="confirm-password-input"
              className={`${authInputClass} pl-9 pr-3`}
            />
          </div>
        </div>

        {/* Terms */}
        <p className="text-xs text-slate-500 text-center">
          By creating an account, you agree to our{' '}
          <button type="button" onClick={() => navigate('/terms')} className="text-blue-600 hover:underline">Terms of Service</button>{' '}
          and{' '}
          <button type="button" onClick={() => navigate('/privacy')} className="text-blue-600 hover:underline">Privacy Policy</button>
        </p>

        <button
          type="submit"
          disabled={loading}
          className="w-full h-12 rounded-2xl font-bold text-base text-white flex items-center justify-center gap-2 bg-gradient-to-r from-[#57B22E] to-[#3E9A1C] shadow-lg shadow-green-600/30 hover:from-[#4CA327] hover:to-[#358916] active:scale-[0.99] transition-all disabled:opacity-60"
          data-testid="register-button"
        >
          {loading ? (
            <div className="spinner-3d w-6 h-6 border-2"></div>
          ) : (
            <>
              <UserPlus size={20} />
              Create Account
              <ArrowRight size={20} />
            </>
          )}
        </button>
      </form>

      {/* Google Sign-Up */}
      <div className="mt-3">
        <GoogleSignInButton label="Sign up with Google" testId="register-google-button" />
      </div>

      {/* Apple Sign-Up (iOS only) */}
      <div className="mt-3">
        <AppleSignInButton label="Sign up with Apple" testId="register-apple-button" />
      </div>

      {/* Divider */}
      <div className="my-3 flex items-center gap-3">
        <div className="flex-1 border-t border-slate-200"></div>
        <span className="text-sm text-slate-500 font-medium whitespace-nowrap">Already have an account?</span>
        <div className="flex-1 border-t border-slate-200"></div>
      </div>

      {/* Login Link */}
      <button
        onClick={() => navigate('/login')}
        className="w-full h-12 rounded-2xl font-bold text-blue-600 border-2 border-blue-500/70 bg-white hover:bg-blue-50 active:scale-[0.99] flex items-center justify-center gap-2 transition-all"
        data-testid="login-link"
      >
        <ArrowLeft size={18} />
        Back to Login
      </button>
    </AuthLayout>
  );
}
